import React, { useState, useEffect } from "react";
import { Dialog, IconButton, TextField, CircularProgress } from "@mui/material";
import { X, Video, Save } from "lucide-react";
import { toast } from "react-hot-toast";

interface AddMeetLinkModalProps {
  open: boolean;
  request: any;
  onClose: () => void;
  onSuccess: () => void;
}

const AddMeetLinkModal: React.FC<AddMeetLinkModalProps> = ({
  open,
  request,
  onClose,
  onSuccess,
}) => {
  const [meetLink, setMeetLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingMeeting, setExistingMeeting] = useState<any>(null);
  const [fetchingMeeting, setFetchingMeeting] = useState(false);

  // Validation URL
  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const fetchExistingMeeting = React.useCallback(async () => {
    setFetchingMeeting(true);
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000"
        }/meetings/request/${request._id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (response.ok) {
        const data = await response.json();
        setExistingMeeting(data);
        setMeetLink(data.link || "");
      } else {
        // Pas de meeting existant
        setExistingMeeting(null);
        setMeetLink("");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du meeting:", error);
    } finally {
      setFetchingMeeting(false);
    }
  }, [request._id]);

  // Récupérer le meeting existant si disponible
  useEffect(() => {
    if (open && request?._id) {
      fetchExistingMeeting();
    }
  }, [open, request?._id, fetchExistingMeeting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!meetLink.trim()) {
      toast.error("Le lien de réunion est requis");
      return;
    }

    // Validation du lien
    if (!isValidUrl(meetLink)) {
      toast.error("Veuillez entrer un lien valide");
      return;
    }

    setLoading(true);
    try {
      let response;

      if (existingMeeting) {
        // Mettre à jour le lien existant
        response = await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5000"
          }/meetings/${existingMeeting._id}/link`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ link: meetLink }),
          },
        );
      } else {
        // Créer un nouveau meeting
        response = await fetch(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5000"
          }/requests/${request._id}/meetings`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              link: meetLink,
            }),
          },
        );
      }

      if (response.ok) {
        toast.success(
          existingMeeting
            ? "Lien mis à jour avec succès !"
            : "Lien ajouté avec succès !",
        );
        onSuccess();
        handleClose();
      } else {
        const error = await response.json();
        toast.error(error.message || "Erreur lors de la sauvegarde du lien");
      }
    } catch (error: any) {
      console.error("Erreur:", error);
      toast.error(error.message || "Erreur lors de la sauvegarde du lien");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMeetLink("");
    setExistingMeeting(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      {/* Header */}
      <div className="relative p-4 sm:p-6 bg-[#E3B500] text-white flex justify-between items-center">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <Video className="w-5 h-5 sm:w-6 sm:h-6" />
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold">
            {existingMeeting
              ? "Modifier le lien de réunion"
              : "Ajouter un lien de réunion"}
          </h2>
        </div>
        <IconButton onClick={handleClose} className="text-white">
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </IconButton>
      </div>

      {/* Contenu */}
      <form onSubmit={handleSubmit}>
        <div className="p-4 sm:p-6">
          {fetchingMeeting ? (
            <div className="flex justify-center py-8">
              <CircularProgress size={40} />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lien de la réunion *
              </label>
              <TextField
                fullWidth
                type="url"
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                value={meetLink}
                onChange={(e) => setMeetLink(e.target.value)}
                required
                disabled={loading}
                variant="outlined"
                size="small"
                className="bg-white"
                error={!!(meetLink && !isValidUrl(meetLink))}
                helperText={
                  meetLink && !isValidUrl(meetLink)
                    ? "Veuillez entrer une URL valide"
                    : ""
                }
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 bg-gray-50 border-t flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-2 sm:order-1"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading || fetchingMeeting}
            className="px-4 py-2 bg-[#E3B500] text-white rounded-lg flex items-center justify-center gap-2 hover:bg-[#CA8A04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-1 sm:order-2"
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" />
                <span>Enregistrement...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>{existingMeeting ? "Mettre à jour" : "Enregistrer"}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddMeetLinkModal;
